"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Building2, CircleCheck as CheckCircle2, CircleX as XCircle, Sparkles, ArrowRight, ShieldCheck, Zap, PiggyBank, Target, MessageSquare as MessageSquareHorizontal, FileChartColumn as FileBarChart } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  title: "Direct Access & Communication",
  iconName: MessageSquareHorizontal,
  consultant: "You work directly with me at all times via email, call, or Slack. No middleman, no junior account managers.",
  agency: "Communications filtered through non-technical account managers and account executives.",
  highlight: "100% Direct Access"
}, {
  id: 2,
  title: "Strategy & Senior Expertise",
  iconName: Target,
  consultant: "Every strategy, audit, and recommendation is researched and crafted personally by me based on hands-on experience.",
  agency: "Pitched by senior directors, but daily work is delegated to junior hires or interns.",
  highlight: "Senior Hands-On Expertise"
}, {
  id: 3,
  title: "Cost Efficiency & Pricing",
  iconName: PiggyBank,
  consultant: "Your entire budget goes into actual SEO strategy and execution, not funding account teams or plush offices.",
  agency: "High retainers with up to 60% allocated to agency overheads, sales commissions, and management fees.",
  highlight: "Maximized ROI"
}, {
  id: 4,
  title: "Agility & Execution Speed",
  iconName: Zap,
  consultant: "Nimble implementation and fast turnarounds without waiting for internal approvals or bureaucratic delay.",
  agency: "Slow turnarounds due to rigid department silos, ticket queues, and multiple management layers.",
  highlight: "Fast Turnaround"
}, {
  id: 5,
  title: "Tailored & Bespoke Approach",
  iconName: Sparkles,
  consultant: "Custom SEO roadmaps tailored specifically to your exact industry, market size, and technical framework.",
  agency: "Standardized checklist packages and automated templates pushed across all client accounts.",
  highlight: "Custom Roadmap"
}, {
  id: 6,
  title: "Transparent & Honest Reporting",
  iconName: FileBarChart,
  consultant: "Clear, commercial-focused reporting tied to organic leads, revenue growth, and actual search performance.",
  agency: "Standard automated PDF exports emphasizing vanity metrics that mask real business impact.",
  highlight: "Revenue-Focused Metrics"
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background py-20 md:py-28 text-foreground transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3.5 py-1 text-sm rounded-full">
            <UserCheck className="w-3.5 h-3.5 mr-1.5 text-accent" />
            Direct Expertise vs Agency Loop
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground mb-6 leading-tight">
            Why Hiring Me Beats Working With an Agency
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire YE Consultancy, you get my direct, uncompromised attention. I deliver tailored, high-impact SEO strategies designed for sustainable search growth—without agency markups or junior delegation.
          </p>
        </div>

        {/* Comparison Cards Grid */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }}>
          {/* Consultant Column */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border-2 border-primary/20 bg-card rounded-2xl shadow-lg relative overflow-hidden flex flex-col justify-between transition-[border-color,box-shadow] duration-300 hover:border-primary/40">
              <div className="absolute top-0 right-0 bg-secondary text-foreground text-xs font-semibold px-4 py-1.5 rounded-bl-xl border-b border-l border-border flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                <span>Personal Commitment</span>
              </div>

              <CardHeader className="pb-4 pt-6 px-6 sm:px-8 border-b border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 rounded-xl bg-primary text-primary-foreground shadow-xs">
                    <UserCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold font-default text-foreground">
                      Working With Me
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                      Independent Freelance SEO Consultant
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 sm:p-8 flex-1 flex flex-col justify-between gap-6">
                <ul className="space-y-6">
                  {comparisonPoints.map((point, index) => {
                  return <li key={`consultant-${point.id}`} data-index={index} className="flex items-start gap-3.5">
                        <div className="mt-1 p-1 rounded-full bg-secondary text-accent shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-accent" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground text-sm sm:text-base">
                              {point.title}
                            </span>
                            <span className="text-[13px] font-medium px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border">
                              {point.highlight}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {point.consultant}
                          </p>
                        </div>
                      </li>;
                })}
                </ul>

                <div className="p-4 rounded-xl bg-muted/60 border border-border mt-4">
                  <p className="text-xs sm:text-sm font-medium text-foreground italic">
                    "My reputation is on the line with every campaign. You will always deal directly with me, ensuring strategic continuity and maximum value."
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 font-semibold">
                    — Yassine Essaadi, SEO Consultant
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Agency Column */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card/60 rounded-2xl shadow-sm flex flex-col justify-between transition-[border-color,box-shadow] duration-300 hover:border-border/80">
              <CardHeader className="pb-4 pt-6 px-6 sm:px-8 border-b border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 rounded-xl bg-muted text-muted-foreground">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold font-default text-foreground">
                      Traditional SEO Agency
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                      Standard Multi-Tiered Agencies
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 sm:p-8 flex-1 flex flex-col justify-between gap-6">
                <ul className="space-y-6">
                  {comparisonPoints.map((point, index) => <li key={`agency-${point.id}`} data-index={index} className="flex items-start gap-3.5">
                      <div className="mt-1 p-1 rounded-full bg-destructive/10 text-destructive shrink-0">
                        <XCircle className="w-5 h-5 text-destructive" />
                      </div>
                      <div className="space-y-1">
                        <span className="font-semibold text-foreground text-sm sm:text-base block">
                          {point.title}
                        </span>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {point.agency}
                        </p>
                      </div>
                    </li>)}
                </ul>

                <div className="p-4 rounded-xl bg-muted/30 border border-border/50 mt-4">
                  <p className="text-xs sm:text-sm text-muted-foreground italic">
                    High operational overheads and frequent staff turnover often lead to inconsistent campaign focus and diluted accountability.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Bottom CTA Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl bg-secondary/80 border border-border p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-md">
          <div className="inline-flex items-center justify-center p-3 bg-background rounded-full mb-4 shadow-xs">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-semibold font-default text-foreground mb-3">
            Ready for Hands-On SEO Leadership?
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Let us discuss your current search performance, growth targets, and how working directly with me can accelerate your organic revenue.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium px-7 py-6 text-base rounded-xl shadow-md">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium px-7 py-6 text-base rounded-xl">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}